import React, { useEffect, useRef, useState } from 'react';
import useGoogleMaps from '../../../hooks/useGoogleMaps';

const GoogleMapComponent = ({ 
  center = { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
  zoom = 12,
  onMapLoad,
  className = "w-full h-full min-h-[400px]",
  showUserMarker = true
}) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { 
    isLoaded, 
    error: mapsError, 
    loadGoogleMaps, 
    createMap, 
    addMarker, 
    setCenter, 
    setZoom,
    cleanup 
  } = useGoogleMaps();

  // Initialize map only once on mount
  useEffect(() => {
    const initializeMap = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load Google Maps API using the hook
        await loadGoogleMaps();

        // Create map instance using the hook
        const map = createMap(mapRef.current, {
          center: center,
          zoom: zoom,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            // Optional: Add custom styling for better integration
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Add user location marker if requested
        if (showUserMarker && center) {
          const marker = addMarker(center, {
            title: 'Your Location',
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="8" fill="#8B5CF6" stroke="#FFFFFF" stroke-width="2"/>
                  <circle cx="12" cy="12" r="3" fill="#FFFFFF"/>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(24, 24),
              anchor: new window.google.maps.Point(12, 12)
            }
          });
          markerRef.current = marker;
        }

        // Call onMapLoad callback if provided
        if (onMapLoad) {
          onMapLoad(map);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing Google Maps:', err);
        setError(err.message || mapsError);
        setIsLoading(false);
      }
    };

    if (mapRef.current) {
      initializeMap();
    }

    // Cleanup function
    return () => {
      cleanup();
    };
  }, []); // Empty dependency array - only run once on mount

  // Update map center when center prop changes
  useEffect(() => {
    if (isLoaded && center) {
      setCenter(center);
      
      // Update marker position if it exists
      if (markerRef.current) {
        markerRef.current.setPosition(center);
      }
    }
  }, [center, isLoaded, setCenter]);

  // Update zoom when zoom prop changes
  useEffect(() => {
    if (isLoaded && zoom) {
      setZoom(zoom);
    }
  }, [zoom, isLoaded, setZoom]);

  if (error) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg`}>
        <div className="text-center p-8">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Map Unavailable
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Map container */}
      <div ref={mapRef} className="w-full h-full rounded-lg" />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMapComponent;